import React, { useEffect, useRef, useState } from "react";
import { BASE_URL } from "../../const";
import Navbar from "../../components/Navbar";

const BRAND_OPTIONS = ["Kyari", "Robotnano"];
const ITEM_TYPES = ["single", "bundle"];
const TAX_RULE_OPTIONS = ["tax_0%", "tax_3%", "tax_5%", "tax_12%", "tax_18%", "tax_28%"];

const COL_DEFS = [
  { key: "sku_reference_code", label: "SKU Reference Code", required: true, width: 150 },
  { key: "parent_sku", label: "Parent SKU", required: true, width: 150 },
  { key: "SKU", label: "SKU", required: true, width: 140 },
  { key: "ModelName", label: "SKU Name", required: true, width: 150 },
  { key: "EANUPC", label: "EAN/UPC", required: true, width: 150 },
  { key: "Brand", label: "Brand", required: true, type: "select", options: BRAND_OPTIONS, width: 110 },
  { key: "Category", label: "Category", required: true, type: "category", width: 150 },
  { key: "Weight", label: "Weight", required: true, width: 80 },
  { key: "Height", label: "Height", required: true, width: 80 },
  { key: "Length", label: "Length", required: true, width: 80 },
  { key: "Width", label: "Width", required: true, width: 80 },
  
  { key: "ProductTaxCode", label: "ProductTaxCode", required: true, width: 110 },
  { key: "TaxRuleName", label: "TaxRuleName", required: true, type: "select", options: TAX_RULE_OPTIONS, width: 110 },
  { key: "Mrp", label: "MRP", required: true, width: 80 },
  { key: "ItemType", label: "ItemType", required: true, type: "select", options: ITEM_TYPES, width: 100 },
  { key: "Components", label: "ComponentSKU/ComponentQty", required: false, type: "components", width: 170 },
  { key: "Description", label: "Description", required: false, width: 160 },
];

function parseList(data) {
  if (Array.isArray(data?.data)) return data.data;
  if (Array.isArray(data?.results)) return data.results;
  if (Array.isArray(data)) return data;
  return [];
}

function rid() {
  return Math.random().toString(36).slice(2, 11);
}

function emptyComponent() {
  return { id: rid(), sku: "", quantity: "" };
}

function ensureTwoComponents(components) {
  const list = Array.isArray(components) ? [...components] : [];
  while (list.length < 2) list.push(emptyComponent());
  return list;
}

function emptyRow() {
  return {
    id: rid(),
    sku_reference_code: "",
    parent_sku: "",
    SKU: "",
    ModelName: "",
    EANUPC: "",
    Brand: "",
    Category: "",
    Weight: "",
    Height: "",
    Length: "",
    Width: "",
    ProductTaxCode: "",
    TaxRuleName: "",
    Mrp : "", 

    ItemType: "single",
    Description: "",
    components: [],
  };
}

function toTaxValue(taxRuleName) {
  const map = {
    "tax_0%": "0",  "tax_5%": "5",
    "tax_18%": "18", "tax_28%": "28",
    "0": "0", "3": "3", "5": "5", "12": "12", "18": "18", "28": "28",
  };
  return map[String(taxRuleName || "").toLowerCase().trim()] || "";
}

function normalizeTaxRuleInput(value) {
  const key = String(value || "").toLowerCase().trim();
  if (!key) return "";
  if (key.startsWith("tax_")) return key;
  if (["0", "3", "5", "12", "18", "28"].includes(key)) return `tax_${key}%`;
  return key;
}

function isBlank(v) {
  return String(v ?? "").trim() === "";
}

function hasAnyValue(row) {
  return COL_DEFS.some((f) => !isBlank(row[f.key])) ||
    (row.components || []).some((c) => !isBlank(c.sku) || !isBlank(c.quantity));
}

function validateRow(row) {
  const errors = [];
  COL_DEFS.forEach((f) => {
    if (f.required && isBlank(row[f.key])) errors.push(`${f.label} is required`);
  });
  if (row.ItemType === "bundle") {
    const active = (row.components || []).filter((c) => !isBlank(c.sku) || !isBlank(c.quantity));
    if (active.length < 1) errors.push("Bundle requires at least 1 component");
    active.forEach((c, i) => {
      if (isBlank(c.sku)) errors.push(`Component ${i + 1} SKU required`);
      if (isBlank(c.quantity)) errors.push(`Component ${i + 1} quantity required`);
      if (!isBlank(c.quantity) && Number(c.quantity) <= 0) errors.push(`Component ${i + 1} qty must be > 0`);
    });
  }
  if (!isBlank(row.TaxRuleName) && !toTaxValue(row.TaxRuleName))
    errors.push("Invalid Tax Rule");
  return errors;
}

function buildApiPayload(row) {
  return {
    sku_reference_code: String(row.sku_reference_code || "").trim(),
    parent_sku: String(row.parent_sku || "").trim(),
    SKU: String(row.SKU || "").trim(),
    ModelName: String(row.ModelName || "").trim(),
    EANUPC: String(row.EANUPC || "").trim(),
    Brand: String(row.Brand || "").trim(),
    Category: String(row.Category || "").trim(),
    Weight: String(row.Weight || "").trim(),
    Height: String(row.Height || "").trim(),
    Length: String(row.Length || "").trim(),
    Width: String(row.Width || "").trim(),
    ProductTaxCode: String(row.ProductTaxCode || "").trim(),
    TaxRuleName: String(row.TaxRuleName || "").trim(),
    Mrp: String(row.Mrp || "").trim(),
    ItemType: String(row.ItemType || "single").trim(),
    Description: String(row.Description || row.ModelName || row.SKU || "").trim(),
    components: (row.components || [])
      .filter((c) => !isBlank(c.sku) && !isBlank(c.quantity))
      .map((c) => ({ sku: String(c.sku).trim(), quantity: Number(c.quantity) })),
  };
}

function parseCSV(text) {
  const rows = [];
  let current = "";
  let line = [];
  let inQuotes = false;
  for (let i = 0; i < text.length; i++) {
    const ch = text[i];
    const next = text[i + 1];
    if (ch === '"' && inQuotes && next === '"') { current += '"'; i++; }
    else if (ch === '"') { inQuotes = !inQuotes; }
    else if (ch === "," && !inQuotes) { line.push(current); current = ""; }
    else if ((ch === "\n" || ch === "\r") && !inQuotes) {
      if (ch === "\r" && next === "\n") i++;
      line.push(current); rows.push(line); current = ""; line = [];
    } else { current += ch; }
  }
  if (current.length > 0 || line.length > 0) { line.push(current); rows.push(line); }
  return rows;
}

function csvValue(obj, ...keys) {
  for (const k of keys) {
    const v = obj[k];
    if (v !== undefined && v !== null && String(v).trim() !== "") return String(v).trim();
  }
  return "";
}

function mapCsvRowToFormRow(rec) {
  const itemRaw = csvValue(rec, "ItemType", "item_type").toLowerCase();
  const itemType = itemRaw === "bundle" || itemRaw === "combo" ? "bundle" : "single";
  const components = [];
  const singleComponentSku = csvValue(rec, "ComponentSKU", "Component SKU");
  const singleComponentQty = csvValue(rec, "ComponentQty", "Component Qty");
  if (!isBlank(singleComponentSku) || !isBlank(singleComponentQty)) {
    components.push({ id: rid(), sku: singleComponentSku, quantity: singleComponentQty });
  }
  for (let i = 1; i <= 5; i++) {
    const sku = csvValue(rec, `ComponentSKU${i}`, `Component SKU ${i}`);
    const qty = csvValue(rec, `ComponentQty${i}`, `Component Qty ${i}`);
    if (!isBlank(sku) || !isBlank(qty)) components.push({ id: rid(), sku, quantity: qty });
  }
  return {
    id: rid(),
    sku_reference_code: csvValue(rec, "sku_reference_code", "SKU Reference Code", "SkuReferenceCode"),
    parent_sku: csvValue(rec, "parent_sku", "Parent SKU"),
    SKU: csvValue(rec, "SKU"),
    ModelName: csvValue(rec, "ModelName", "Model Name"),
    EANUPC: csvValue(rec, "EANUPC", "EAN/UPC"),
    Brand: csvValue(rec, "Brand"),
    Category: csvValue(rec, "Category"),
    Weight: csvValue(rec, "Weight"),
    Height: csvValue(rec, "Height"),
    Length: csvValue(rec, "Length"),
    Width: csvValue(rec, "Width"),
    ProductTaxCode: csvValue(rec, "ProductTaxCode", "HSN Code", "HSN"),
    TaxRuleName: normalizeTaxRuleInput(csvValue(rec, "TaxRuleName", "Tax Rule", "Tax Rate")),
    Mrp: csvValue(rec, "Mrp", "MRP"),
    ItemType: itemType,
    Description: csvValue(rec, "Description"),
    components: itemType === "bundle" ? ensureTwoComponents(components) : [],
  };
}

function downloadSampleCsv() {
  const headers = [
    "sku_reference_code", "parent_sku", "SKU", "ModelName", "EANUPC",
    "Brand", "Category", "Weight", "Height", "Length", "Width", "ProductTaxCode",
    "TaxRuleName","MRP", "ItemType", "Description", "ComponentSKU", "ComponentQty"
  ];
  const sample = [
    "ACC-1001", "NPD-030426-KWPT", "NPD-030426-KWPT", "Sample Product Bundle", "123456789012",
    "Kyari", "KPW poetic 4-inch", "1.2", "10", "20", "8", "393995", "tax_5%","999", "bundle", "Sample description", "A101", "1"
  ];
  const csv = `${headers.join(",")}\n${sample.join(",")}`;
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = "sku_creation_sample.csv";
  document.body.appendChild(a); a.click();
  document.body.removeChild(a); URL.revokeObjectURL(url);
}

/* ─── Progress Modal ─── */
function ProgressModal({ progress, total, log, apiResponseText, onDone }) {
  const done = progress === total;
  const created = log.filter((l) => l.status === "CREATED").length;
  const failed = log.filter((l) => l.status === "FAILED").length;
  const pct = total > 0 ? Math.round((progress / total) * 100) : 0;

  return (
    <div style={ms.overlay}>
      <div style={ms.modal}>
        <div style={ms.modalHead}>
          <span style={ms.modalTitle}>{done ? "Creation Complete" : "Creating SKUs…"}</span>
        </div>

        <div style={ms.statsRow}>
          <div style={ms.statBox}>
            <span style={ms.statNum}>{total}</span>
            <span style={ms.statLbl}>Total</span>
          </div>
          <div style={{ ...ms.statBox, ...ms.statGreen }}>
            <span style={ms.statNum}>{created}</span>
            <span style={ms.statLbl}>Created</span>
          </div>
          <div style={{ ...ms.statBox, ...ms.statRed }}>
            <span style={ms.statNum}>{failed}</span>
            <span style={ms.statLbl}>Failed</span>
          </div>
          <div style={ms.statBox}>
            <span style={ms.statNum}>{total - progress}</span>
            <span style={ms.statLbl}>Remaining</span>
          </div>
        </div>

        <div style={ms.progressTrack}>
          <div style={{ ...ms.progressBar, width: `${pct}%` }} />
        </div>
        <div style={ms.pctLabel}>{pct}%</div>

        {log.length > 0 && (
          <div style={ms.logScroll}>
            {log.map((item, idx) => (
              <div key={idx} style={ms.logItem}>
                <span style={ms.logSku}>{item.sku}</span>
                <span style={item.status === "CREATED" ? ms.tagOk : ms.tagFail}>{item.status}</span>
                {item.error && <span style={ms.logErr}>{item.error}</span>}
              </div>
            ))}
          </div>
        )}

        {apiResponseText && (
          <div style={ms.outputWrap}>
            <div style={ms.outputTitle}>API Response Output</div>
            <pre style={ms.outputPre}>{apiResponseText}</pre>
          </div>
        )}

        {done && (
          <button style={ms.doneBtn} onClick={onDone}>Close</button>
        )}
      </div>
    </div>
  );
}

const ms = {
  overlay: { position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 },
  modal: { background: "#fff", borderRadius: 14, padding: 28, width: 560, maxWidth: "95vw", maxHeight: "85vh", display: "flex", flexDirection: "column", gap: 16 },
  modalHead: { borderBottom: "1px solid #e5e7eb", paddingBottom: 12 },
  modalTitle: { fontSize: 17, fontWeight: 700, color: "#0f172a" },
  statsRow: { display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 10 },
  statBox: { background: "#f8fafc", border: "1px solid #e5e7eb", borderRadius: 10, padding: "10px 8px", textAlign: "center" },
  statGreen: { background: "#f0fdf4", borderColor: "#bbf7d0" },
  statRed: { background: "#fef2f2", borderColor: "#fecaca" },
  statNum: { display: "block", fontSize: 22, fontWeight: 700, color: "#0f172a" },
  statLbl: { display: "block", fontSize: 11, color: "#6b7280", marginTop: 2 },
  progressTrack: { background: "#f1f5f9", borderRadius: 99, height: 10, overflow: "hidden" },
  progressBar: { height: "100%", background: "#0f172a", borderRadius: 99, transition: "width 0.3s ease" },
  pctLabel: { textAlign: "center", fontSize: 12, color: "#6b7280", marginTop: -8 },
  logScroll: { flex: 1, overflowY: "auto", border: "1px solid #f1f5f9", borderRadius: 8, maxHeight: 240 },
  logItem: { display: "grid", gridTemplateColumns: "auto 80px 1fr", gap: 10, padding: "7px 12px", borderBottom: "1px solid #f8fafc", fontSize: 12, alignItems: "center" },
  logSku: { fontWeight: 600, color: "#111827" },
  tagOk: { background: "#dcfce7", color: "#15803d", borderRadius: 6, padding: "2px 8px", fontSize: 11, fontWeight: 700, textAlign: "center" },
  tagFail: { background: "#fee2e2", color: "#b91c1c", borderRadius: 6, padding: "2px 8px", fontSize: 11, fontWeight: 700, textAlign: "center" },
  logErr: { color: "#6b7280", fontSize: 11 },
  outputWrap: { border: "1px solid #e5e7eb", borderRadius: 8, overflow: "hidden", background: "#fff" },
  outputTitle: { fontSize: 12, fontWeight: 700, color: "#334155", background: "#f8fafc", borderBottom: "1px solid #e5e7eb", padding: "8px 10px" },
  outputPre: { margin: 0, padding: "10px", fontSize: 11, maxHeight: 180, overflow: "auto", background: "#0b1220", color: "#e2e8f0", whiteSpace: "pre-wrap", wordBreak: "break-word" },
  doneBtn: { alignSelf: "flex-end", background: "#0f172a", color: "#fff", border: "none", borderRadius: 8, padding: "9px 22px", cursor: "pointer", fontWeight: 600 },
};

/* ─── Error Panel ─── */
function ErrorPanel({ failedLog, rows, onRemoveRow }) {
  if (!failedLog.length) return null;
  return (
    <div style={ep.box}>
      <div style={ep.head}>
        <span style={ep.title}>Failed SKUs ({failedLog.length})</span>
        <span style={ep.sub}>Fix errors and retry</span>
      </div>
      {failedLog.map((item, idx) => (
        <div key={idx} style={ep.row}>
          <div>
            <span style={ep.sku}>{item.sku}</span>
            <span style={ep.err}>{item.error}</span>
          </div>
          <button style={ep.removeBtn} onClick={() => onRemoveRow(item.rowId)}>Remove row</button>
        </div>
      ))}
    </div>
  );
}

const ep = {
  box: { marginTop: 16, border: "1px solid #fecaca", borderRadius: 10, background: "#fff", overflow: "hidden" },
  head: { display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 14px", background: "#fef2f2", borderBottom: "1px solid #fecaca" },
  title: { fontWeight: 700, color: "#b91c1c", fontSize: 14 },
  sub: { fontSize: 12, color: "#9ca3af" },
  row: { display: "flex", justifyContent: "space-between", alignItems: "center", padding: "9px 14px", borderBottom: "1px solid #fee2e2", fontSize: 13 },
  sku: { fontWeight: 700, color: "#111827", marginRight: 12 },
  err: { color: "#b91c1c", fontSize: 12 },
  removeBtn: { border: "1px solid #fecaca", background: "#fff", color: "#b91c1c", borderRadius: 6, padding: "4px 10px", cursor: "pointer", fontSize: 12 },
};

/* ─── Components Popover ─── */
function ComponentsPopover({ row, onClose, onSetComponent, onAddComponent, onRemoveComponent, showValidation }) {
  const overlayRef = useRef(null);
  const components = row.components || [];

  function handleOverlayClick(e) {
    if (e.target === overlayRef.current) onClose();
  }

  return (
    <div ref={overlayRef} style={cp.overlay} onClick={handleOverlayClick}>
      <div style={cp.box}>
        <div style={cp.head}>
          <div>
            <span style={cp.title}>Bundle Components</span>
            <span style={cp.sub}> — {row.SKU || "this row"}</span>
          </div>
          <button style={cp.closeBtn} onClick={onClose}>✕</button>
        </div>

        <div style={cp.tableWrap}>
          <table style={cp.table}>
            <thead>
              <tr>
                <th style={cp.th}>#</th>
                <th style={cp.th}>Component SKU *</th>
                <th style={cp.th}>Quantity *</th>
                <th style={cp.th} />
              </tr>
            </thead>
            <tbody>
              {components.map((comp, ci) => {
                const missing = showValidation && (isBlank(comp.sku) || isBlank(comp.quantity));
                return (
                  <tr key={comp.id}>
                    <td style={cp.td}><span style={cp.idx}>{ci + 1}</span></td>
                    <td style={cp.td}>
                      <input
                        style={missing ? { ...cp.input, ...cp.inputErr } : cp.input}
                        placeholder="e.g. A101"
                        value={comp.sku}
                        onChange={(e) => onSetComponent(comp.id, "sku", e.target.value)}
                      />
                    </td>
                    <td style={cp.td}>
                      <input
                        style={missing ? { ...cp.input, ...cp.inputErr } : cp.input}
                        placeholder="1"
                        value={comp.quantity}
                        onChange={(e) => onSetComponent(comp.id, "quantity", e.target.value)}
                      />
                    </td>
                    <td style={cp.td}>
                      <button style={cp.removeBtn} onClick={() => onRemoveComponent(comp.id)}>Remove</button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <div style={cp.footer}>
          <button style={cp.addBtn} onClick={onAddComponent}>+ Add Component</button>
          <button style={cp.doneBtn} onClick={onClose}>Done</button>
        </div>
      </div>
    </div>
  );
}

const cp = {
  overlay: { position: "fixed", inset: 0, background: "rgba(0,0,0,0.35)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 900 },
  box: { background: "#fff", borderRadius: 12, width: 480, maxWidth: "95vw", boxShadow: "0 8px 32px rgba(0,0,0,0.18)", overflow: "hidden", display: "flex", flexDirection: "column" },
  head: { display: "flex", justifyContent: "space-between", alignItems: "center", padding: "14px 16px", borderBottom: "1px solid #e5e7eb", background: "#f9fafb" },
  title: { fontWeight: 700, fontSize: 14, color: "#0f172a" },
  sub: { fontSize: 13, color: "#9ca3af" },
  closeBtn: { border: "none", background: "none", color: "#6b7280", cursor: "pointer", fontSize: 16, padding: "2px 6px" },
  tableWrap: { padding: "12px 16px", overflowY: "auto", maxHeight: 320 },
  table: { width: "100%", borderCollapse: "collapse" },
  th: { padding: "6px 8px", fontSize: 11, fontWeight: 700, color: "#6b7280", textAlign: "left", borderBottom: "1px solid #e5e7eb" },
  td: { padding: "5px 8px", borderBottom: "1px solid #f1f5f9", verticalAlign: "middle" },
  idx: { fontSize: 12, color: "#9ca3af" },
  input: { width: "100%", border: "1px solid #d1d5db", borderRadius: 6, padding: "5px 8px", fontSize: 13, color: "#111827", background: "#fff", outline: "none", boxSizing: "border-box" },
  inputErr: { borderColor: "#ef4444", background: "#fef2f2" },
  footer: { display: "flex", justifyContent: "space-between", alignItems: "center", padding: "12px 16px", borderTop: "1px solid #e5e7eb", background: "#f9fafb" },
  addBtn: { border: "1px solid #c7d2fe", borderRadius: 7, background: "#eef2ff", color: "#4338ca", padding: "6px 14px", cursor: "pointer", fontSize: 13, fontWeight: 600 },
  doneBtn: { border: "none", borderRadius: 7, background: "#0f172a", color: "#fff", padding: "6px 18px", cursor: "pointer", fontSize: 13, fontWeight: 600 },
  removeBtn: { border: "1px solid #fecaca", borderRadius: 5, background: "#fff", color: "#b91c1c", padding: "3px 8px", cursor: "pointer", fontSize: 12 },
};

/* ─── Main Component ─── */
export default function SkuCreation() {
  const [rows, setRows] = useState([emptyRow()]);
  const [categories, setCategories] = useState([]);
  const [showValidation, setShowValidation] = useState(false);
  const [importMessage, setImportMessage] = useState("");
  const [showProgress, setShowProgress] = useState(false);
  const [progress, setProgress] = useState(0);
  const [totalToProcess, setTotalToProcess] = useState(0);
  const [log, setLog] = useState([]);
  const [apiResponseText, setApiResponseText] = useState("");
  const [failedLog, setFailedLog] = useState([]);
  const [openPopoverRowId, setOpenPopoverRowId] = useState(null);
  const fileInputRef = useRef(null);

  useEffect(() => { loadCategories(); }, []);

  async function loadCategories() {
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(`${BASE_URL}/categories/`, { headers: { Authorization: token ? `Token ${token}` : "" } });
      const json = await res.json();
      if (!res.ok) throw new Error(json?.message || "Category load failed");
      setCategories(parseList(json));
    } catch (err) { console.error(err); }
  }

  function setField(rowId, key, value) {
    setRows((prev) =>
      prev.map((r) => {
        if (r.id !== rowId) return r;
        if (key === "ItemType") {
          return { ...r, ItemType: value, components: value === "bundle" ? ensureTwoComponents(r.components) : [] };
        }
        return { ...r, [key]: value };
      })
    );
  }

  function addRow() { setRows((prev) => [...prev, emptyRow()]); }

  function duplicateRow(rowId) {
    setRows((prev) => {
      const idx = prev.findIndex((r) => r.id === rowId);
      if (idx < 0) return prev;
      const src = prev[idx];
      const copy = { ...src, id: rid(), components: (src.components || []).map((c) => ({ ...c, id: rid() })) };
      return [...prev.slice(0, idx + 1), copy, ...prev.slice(idx + 1)];
    });
  }

  function deleteRow(rowId) {
    setRows((prev) => {
      const next = prev.filter((r) => r.id !== rowId);
      return next.length ? next : [emptyRow()];
    });
    setFailedLog((prev) => prev.filter((l) => l.rowId !== rowId));
  }

  function setComponent(rowId, compId, key, value) {
    setRows((prev) =>
      prev.map((r) => r.id !== rowId ? r : {
        ...r,
        components: (r.components || []).map((c) => c.id === compId ? { ...c, [key]: value } : c),
      })
    );
  }

  function addComponent(rowId) {
    setRows((prev) =>
      prev.map((r) => r.id !== rowId ? r : { ...r, components: [...(r.components || []), emptyComponent()] })
    );
  }

  function removeComponent(rowId, compId) {
    setRows((prev) =>
      prev.map((r) => r.id !== rowId ? r : {
        ...r,
        components: ensureTwoComponents((r.components || []).filter((c) => c.id !== compId)),
      })
    );
  }

  async function submitRowsForCreation(activeRows) {
    if (!activeRows.length) { alert("Please add at least one row."); return; }

    setShowValidation(true);
    const invalid = activeRows.filter((r) => validateRow(r).length > 0);
    if (invalid.length) {
      alert(`Validation failed in ${invalid.length} row(s). Please fill highlighted fields.`);
      return;
    }

    setLog([]);
    setApiResponseText("");
    setFailedLog([]);
    setProgress(0);
    setTotalToProcess(activeRows.length);
    setShowProgress(true);

    try {
      const token = localStorage.getItem("token");
      const requestPayload = activeRows.map(buildApiPayload);
      const res = await fetch(`${BASE_URL}/sku/create-easyecom/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: token ? `Token ${token}` : "",
        },
        body: JSON.stringify({ items: requestPayload }),
      });

      const json = await res.json().catch(() => ({}));
      setApiResponseText(JSON.stringify(json, null, 2));
      const rowsResult = Array.isArray(json?.data?.results) ? json.data.results : [];
      const createdSkus = new Set(
        rowsResult
          .filter((item) => item?.status === "CREATED")
          .map((item) => String(item.sku || "").trim())
          .filter(Boolean)
      );

      const nextLog = rowsResult.map((item) => ({
        sku: item?.sku || "-",
        status: item?.status || "FAILED",
        error: item?.message || "",
        rowId: activeRows.find((r) => String(r.SKU || "").trim() === String(item?.sku || "").trim())?.id || null,
      }));

      const newFailed = nextLog.filter((item) => item.status !== "CREATED");
      setLog(nextLog);
      setFailedLog(newFailed);
      setProgress(activeRows.length);

      if (createdSkus.size > 0) {
        setRows((prev) => {
          const remaining = prev.filter((r) => !createdSkus.has(String(r.SKU || "").trim()));
          return remaining.length ? remaining : [emptyRow()];
        });
      }

      if (!res.ok && !rowsResult.length) {
        throw new Error(json?.message || "SKU creation failed");
      }
    } catch (err) {
      setApiResponseText(JSON.stringify({ error: err?.message || "SKU creation failed" }, null, 2));
      setLog((prev) => prev.length ? prev : [{ sku: "-", status: "FAILED", error: err?.message || "SKU creation failed", rowId: null }]);
      setProgress(activeRows.length);
      alert(err?.message || "SKU creation failed");
    }
  }

  async function createSkus() {
    const activeRows = rows.filter(hasAnyValue);
    await submitRowsForCreation(activeRows);
  }

  function handleProgressDone() {
    setShowProgress(false);
  }

  function onClickImport() { if (fileInputRef.current) fileInputRef.current.click(); }

  async function onImportFile(event) {
    const file = event.target.files?.[0];
    if (!file) return;
    try {
      const text = await file.text();
      const matrix = parseCSV(text);
      if (!matrix.length) { setImportMessage("Imported file is empty."); return; }
      const headers = matrix[0].map((h) => String(h || "").trim());
      const records = matrix.slice(1)
        .filter((line) => line.some((cell) => String(cell || "").trim() !== ""))
        .map((line) => {
          const obj = {};
          headers.forEach((h, idx) => { obj[h] = line[idx] || ""; });
          return obj;
        });
      if (!records.length) { setImportMessage("No data rows found."); return; }
      const parsedRows = records.map(mapCsvRowToFormRow);
      const grouped = new Map();
      parsedRows.forEach((row) => {
        const sku = String(row.SKU || "").trim();
        if (!sku) {
          grouped.set(row.id, row);
          return;
        }
        if (!grouped.has(sku)) {
          grouped.set(sku, row);
          return;
        }
        const existing = grouped.get(sku);
        const mergedComponents = [...(existing.components || []), ...(row.components || [])];
        grouped.set(sku, {
          ...existing,
          sku_reference_code: existing.sku_reference_code || row.sku_reference_code,
          parent_sku: existing.parent_sku || row.parent_sku,
          ModelName: existing.ModelName || row.ModelName,
          Brand: existing.Brand || row.Brand,
          Category: existing.Category || row.Category,
          Weight: existing.Weight || row.Weight,
          Height: existing.Height || row.Height,
          Length: existing.Length || row.Length,
          Width: existing.Width || row.Width,
          ProductTaxCode: existing.ProductTaxCode || row.ProductTaxCode,
          TaxRuleName: existing.TaxRuleName || row.TaxRuleName,
          ItemType: existing.ItemType === "bundle" || row.ItemType === "bundle" ? "bundle" : existing.ItemType,
          Description: existing.Description || row.Description,
          components: ensureTwoComponents(
            mergedComponents.filter((c) => !isBlank(c.sku) || !isBlank(c.quantity))
          ),
        });
      });
      const importedRows = Array.from(grouped.values());
      setRows(importedRows.length ? importedRows : [emptyRow()]);
      setImportMessage(`Imported ${importedRows.length} SKU row(s) from ${file.name}. Starting creation...`);
      await submitRowsForCreation(importedRows.filter(hasAnyValue));
    } catch (err) {
      setImportMessage(`Import failed: ${err?.message || "invalid file"}`);
    } finally { event.target.value = ""; }
  }

  const colInput = (row, col) => {
    const missing = showValidation && col.required && isBlank(row[col.key]);
    const style = { ...ts.cell, ...(missing ? ts.cellErr : {}) };

    if (col.type === "select") {
      return (
        <select style={style} value={row[col.key]} onChange={(e) => setField(row.id, col.key, e.target.value)}>
          <option value="">-</option>
          {col.options.map((o) => <option key={o} value={o}>{o}</option>)}
        </select>
      );
    }

    if (col.type === "components") {
      if (row.ItemType !== "bundle") {
        return <span style={{ fontSize: 11, color: "#d1d5db" }}>-</span>;
      }
      const count = (row.components || []).filter((c) => !isBlank(c.sku)).length;
      const hasError = showValidation && validateRow(row).some((e) => e.toLowerCase().includes("component"));
      return (
        <button
          style={{ ...ts.cell, cursor: "pointer", textAlign: "left", background: hasError ? "#fef2f2" : "#f5f3ff", border: `1px solid ${hasError ? "#ef4444" : "#c7d2fe"}`, color: hasError ? "#b91c1c" : "#4338ca", fontWeight: 600, fontSize: 11 }}
          onClick={() => setOpenPopoverRowId(row.id)}
        >
          {count > 0 ? `${count} component${count > 1 ? "s" : ""}` : "+ Add components"}
        </button>
      );
    }

    if (col.type === "category") {
      return (
        <select style={style} value={row[col.key]} onChange={(e) => setField(row.id, col.key, e.target.value)}>
          <option value="">-</option>
          {categories.map((cat) => <option key={cat.id} value={cat.category}>{cat.category}</option>)}
        </select>
      );
    }

    return (
      <input style={style} value={row[col.key]} onChange={(e) => setField(row.id, col.key, e.target.value)} />
    );
  };

  const rowErrors = (row) => showValidation ? validateRow(row) : [];

  return (
    <div style={s.page}>
      <Navbar />
      {/* Header */}
      <div style={s.header}>
        <div>
          <h2 style={s.title}>SKU Creation</h2>
          <p style={s.sub}>Row-wise table with bundle expansion · CSV import supported</p>
        </div>
        <div style={s.headerActions}>
          <button style={s.ghostBtn} onClick={downloadSampleCsv}>↓ Sample CSV</button>
          <button style={s.ghostBtn} onClick={onClickImport}>Import CSV</button>
          <button style={s.primaryBtn} onClick={createSkus}>Create SKUs</button>
          <input ref={fileInputRef} type="file" accept=".csv,text/csv" onChange={onImportFile} style={{ display: "none" }} />
        </div>
      </div>

      {importMessage && <div style={s.importMsg}>{importMessage}</div>}

      {/* Table */}
      <div style={s.tableWrap}>
        <table style={s.table}>
          <thead>
            <tr>
              <th style={{ ...ts.th, width: 32 }}>#</th>
              {COL_DEFS.map((c) => (
                <th key={c.key} style={{ ...ts.th, minWidth: c.width }}>
                  {c.label}{c.required ? <span style={ts.req}> *</span> : ""}
                </th>
              ))}
              <th style={{ ...ts.th, width: 100 }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row, idx) => {
              const errs = rowErrors(row);
              const isBundle = row.ItemType === "bundle";
              const rowBg = errs.length ? "#fef2f2" : "#fff";
              return (
                <React.Fragment key={row.id}>
                  {/* Main data row */}
                  <tr style={{ background: rowBg }}>
                    <td style={{ ...ts.td, color: "#9ca3af", fontSize: 12, textAlign: "center" }}>{idx + 1}</td>
                    {COL_DEFS.map((col) => (
                      <td key={col.key} style={ts.td}>{colInput(row, col)}</td>
                    ))}
                    <td style={ts.td}>
                      <div style={ts.actions}>
                        <button style={ts.dupBtn} onClick={() => duplicateRow(row.id)} title="Duplicate">⧉</button>
                        <button style={ts.delBtn} onClick={() => deleteRow(row.id)} title="Delete">✕</button>
                      </div>
                    </td>
                  </tr>

                  {/* Validation error row */}
                  {errs.length > 0 && (
                    <tr style={{ background: "#fef2f2" }}>
                      <td />
                      <td colSpan={COL_DEFS.length + 1} style={{ padding: "4px 10px 6px", fontSize: 11, color: "#b91c1c" }}>
                        {errs[0]}
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Add row */}
      <div style={s.footer}>
        <button style={s.addRowBtn} onClick={addRow}>+ Add Row</button>
      </div>

      {/* Failed SKU error panel */}
      <ErrorPanel failedLog={failedLog} rows={rows} onRemoveRow={deleteRow} />

      {/* Progress modal */}
      {showProgress && (
        <ProgressModal
          progress={progress}
          total={totalToProcess}
          log={log}
          apiResponseText={apiResponseText}
          onDone={handleProgressDone}
        />
      )}
      {/* Components popover */}
      {openPopoverRowId && (() => {
        const popRow = rows.find((r) => r.id === openPopoverRowId);
        if (!popRow) return null;
        return (
          <ComponentsPopover
            row={popRow}
            showValidation={showValidation}
            onClose={() => setOpenPopoverRowId(null)}
            onSetComponent={(compId, key, value) => setComponent(popRow.id, compId, key, value)}
            onAddComponent={() => addComponent(popRow.id)}
            onRemoveComponent={(compId) => removeComponent(popRow.id, compId)}
          />
        );
      })()}
    </div>
  );
}

/* ─── Styles ─── */
const s = {
  page: { padding: "20px 24px", background: "#f8fafc", minHeight: "100vh", color: "#111827", fontFamily: "system-ui, sans-serif" },
  header: { display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 16, marginBottom: 14 },
  title: { margin: 0, fontSize: 22, fontWeight: 700, color: "#0f172a" },
  sub: { margin: "4px 0 0", color: "#6b7280", fontSize: 13 },
  headerActions: { display: "flex", gap: 8, flexShrink: 0 },
  primaryBtn: { border: "none", borderRadius: 8, background: "#0f172a", color: "#fff", padding: "9px 16px", cursor: "pointer", fontWeight: 600, fontSize: 13 },
  ghostBtn: { border: "1px solid #d1d5db", borderRadius: 8, background: "#fff", color: "#374151", padding: "8px 14px", cursor: "pointer", fontWeight: 600, fontSize: 13 },
  importMsg: { marginBottom: 10, color: "#1d4ed8", fontSize: 13, padding: "8px 12px", background: "#eff6ff", borderRadius: 8, border: "1px solid #bfdbfe" },
  tableWrap: { overflowX: "auto", borderRadius: 10, border: "1px solid #e5e7eb", background: "#fff" },
  table: { width: "100%", borderCollapse: "collapse", tableLayout: "auto" },
  footer: { marginTop: 10 },
  addRowBtn: { border: "1px dashed #d1d5db", borderRadius: 8, background: "#fff", color: "#374151", padding: "8px 16px", cursor: "pointer", fontSize: 13, width: "100%", textAlign: "center" },
};

const ts = {
  th: { padding: "9px 10px", fontSize: 11, fontWeight: 700, color: "#6b7280", background: "#f8fafc", borderBottom: "1px solid #e5e7eb", textAlign: "left", whiteSpace: "nowrap" },
  req: { color: "#ef4444" },
  td: { padding: "5px 6px", borderBottom: "1px solid #f1f5f9", verticalAlign: "middle" },
  cell: { width: "100%", border: "1px solid #e5e7eb", borderRadius: 6, padding: "6px 8px", fontSize: 12, color: "#111827", background: "#fff", outline: "none", boxSizing: "border-box" },
  cellErr: { borderColor: "#ef4444", background: "#fef2f2" },
  actions: { display: "flex", gap: 4, justifyContent: "center" },
  dupBtn: { border: "1px solid #e5e7eb", borderRadius: 5, background: "#fff", color: "#374151", padding: "4px 7px", cursor: "pointer", fontSize: 13 },
  delBtn: { border: "1px solid #fecaca", borderRadius: 5, background: "#fff", color: "#b91c1c", padding: "4px 7px", cursor: "pointer", fontSize: 12 },
};
